import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '@/utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Update ad-free status globally for index.html script
const updateAdFreeStatus = (user) => {
  if (user?.ad_free_until && new Date(user.ad_free_until) > new Date()) {
    window.__adFreeActive = true;
    localStorage.setItem('dragon_tv_ad_free_until', user.ad_free_until);
  } else {
    window.__adFreeActive = false;
    localStorage.removeItem('dragon_tv_ad_free_until');
  }
  // Trigger visibility update
  if (window.__updateAdVisibility) {
    window.__updateAdVisibility();
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('dragon_tv_token');
    if (token) {
      try {
        const response = await authAPI.getMe();
        setUser(response.data);
        updateAdFreeStatus(response.data);
      } catch (error) {
        localStorage.removeItem('dragon_tv_token');
        localStorage.removeItem('dragon_tv_ad_free_until');
        setUser(null);
      }
    }
    setLoading(false);
  };

  const login = async (email, password, securityCode) => {
    const response = await authAPI.login({ email, password, security_code: securityCode });
    localStorage.setItem('dragon_tv_token', response.data.access_token);
    setUser(response.data.user);
    updateAdFreeStatus(response.data.user);
    return response.data;
  };

  const register = async (email, username, password) => {
    const response = await authAPI.register({ email, username, password });
    localStorage.setItem('dragon_tv_token', response.data.access_token);
    setUser(response.data.user);
    updateAdFreeStatus(response.data.user);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('dragon_tv_token');
    localStorage.removeItem('dragon_tv_ad_free_until');
    window.__adFreeActive = false;
    if (window.__updateAdVisibility) {
      window.__updateAdVisibility();
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, refreshUser: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
